import { BooleanInput, Create, CreateContextProvider, DateTimeInput, Form, required, SaveButton, SelectInput, TextInput, Toolbar, TopToolbar, useCreateController, useNotify, useRecordContext, useRedirect, useTranslate } from 'react-admin';
import { Box, Card, Divider, Stack, Typography, Button as MUIButton, Tooltip, IconButton } from '@mui/material';
import { Resources } from '../resource';

import CustomTagInput from 'src/pages/admin/components/CustomTagInput';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useSmartMoneyMetadata from './useSmartMoneyMetadata';
import { chains } from 'src/assets/data';

interface FormProps {
    isCreate: boolean;
}
const SmartMoneyForm = (props: FormProps) => {
    const { isCreate } = props;
    const t = useTranslate();
    const redirect = useRedirect();
    const { watch, setValue, trigger } = useFormContext();
    const record = useRecordContext();
    const metadata = watch('metadata') || record?.metadata || "";
    const { findSmartMoney } = useSmartMoneyMetadata();
    const [addressMessage, setAddressMessage] = useState("")
    const chainId = useWatch({ name: 'chainId' });
    const address = useWatch({ name: 'address' });
    useEffect(() => {
        isCreate && handleOnBlurAddress()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chainId]);
    useEffect(() => {
        if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
            setValue('metadata', JSON.stringify(metadata, null, 2));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata]);

    const handleOnBlurAddress = async () => {
        setAddressMessage("");
        if (!address?.trim() || chainId === "" || chainId === undefined) {
            return
        }

        try {
            const smartMoney = await findSmartMoney(address, chainId);
            if (!smartMoney) {
                return;
            }
            setAddressMessage(t('module.smartMoney.validate.conflict'));
        } catch (error: any) {
            // setAddressMessage(t('module.smartMoney.validate.addressNotExists'))
        }
    };

    const hasTypedRef = useRef(false);

    return (
        <>
            <Stack spacing={3} gap={2}>

                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={1}>
                    <Typography
                        variant="body2"
                        sx={{ flexBasis: '20%', flexShrink: 0 }}
                    >
                        {t("module.smartMoney.address")}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                        <TextInput
                            source="address"
                            label=""
                            placeholder={t("module.smartMoney.address")}
                            validate={[
                                required(),
                                () => {
                                    if (addressMessage !== "") {
                                        return addressMessage;
                                    }
                                    return undefined;
                                }
                            ]}
                            disabled={!isCreate}
                            fullWidth
                            helperText={<span style={{ color: 'red' }}>{addressMessage}</span>}
                            onKeyDown={e => {
                                if (e.key === ' ') {
                                    e.preventDefault();
                                }
                            }}
                            onChange={() => {
                                hasTypedRef.current = true;
                            }}
                            onBlur={(e) => {
                                if (isCreate && hasTypedRef.current) {
                                    handleOnBlurAddress();
                                    hasTypedRef.current = false;
                                }
                            }}

                        />
                    </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={1}>
                    <Typography
                        variant="body2"
                        sx={{ flexBasis: '20%', flexShrink: 0 }}
                    >
                        {t("module.smartMoney.chain")}
                    </Typography>
                    <Box
                        sx={{ flexGrow: 1 }}
                    >
                        <SelectInput
                            label={false}
                            disabled={!isCreate}
                            source="chainId"
                            defaultValue={501424}
                            validate={[required()]}
                            fullWidth
                            helperText={<span style={{ color: 'red' }}></span>}
                            choices={chains}
                        />
                    </Box>

                </Stack>


                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={1}>
                    <Typography
                        variant="body2"
                        sx={{ flexBasis: '20%', flexShrink: 0 }}
                    >
                        {t("module.smartMoney.alias")}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                        <TextInput
                            source="alias"
                            label=""
                            validate={[required()]}
                            placeholder={t("module.smartMoney.alias")}
                            fullWidth
                            helperText={<span style={{ color: 'red' }}></span>}

                        />
                    </Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={1}>
                    <Typography
                        variant="body2"
                        sx={{ flexBasis: '20%', flexShrink: 0 }}
                    >
                        {t("module.smartMoney.avatar")}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                        <TextInput
                            source="avatar"
                            label=""
                            placeholder={t("module.smartMoney.avatar")}
                            fullWidth
                            helperText={<span style={{ color: 'red' }}></span>}

                        />
                    </Box>
                </Stack>
                <Stack direction={'row'} alignItems={'center'} spacing={2} mb={1}>
                    <Typography
                        variant="body2"
                        sx={{ flexBasis: '20%', flexShrink: 0 }}
                    >
                        {t("module.smartMoney.enable")}
                    </Typography>
                    <Box
                        sx={{ flexGrow: 1 }}
                    >
                        <BooleanInput label='' source="enable" helperText={<span style={{ color: 'red' }}></span>} />
                    </Box>

                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={1}>
                    <Box sx={{ flexBasis: '20%', flexShrink: 0, mt: -1 }}>
                        <Typography variant="body2">{t("module.smartMoney.metadata")} (Optional)</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Json Here
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <TextInput
                            source="metadata"
                            label=""
                            placeholder={t("module.smartMoney.metadata")}
                            fullWidth
                            multiline
                            helperText={<span style={{ color: 'red' }}></span>}
                            rows={4}
                        />
                    </Box>
                </Stack>
            </Stack>
            </>
    );
};

export default SmartMoneyForm;
